import type { OrderReturn } from '@/types/orderReturns';

/**
 * Base template for mock OrderReturn data based on real return structure
 */
const getBaseReturnTemplate = (): Omit<OrderReturn, 'ta_returnrequestid'> => ({
	ta_name: 'Return Request',
	ta_streetaddress: '123 Main Street',
	ta_city: 'New York',
	ta_status: 953870000, // Pending status code
	statuscode: 1,
	statecode: 0,
	createdon: new Date().toISOString(),
	modifiedon: new Date().toISOString(),
	_ta_customername_value: '{61aea445-e3b4-ea11-a816-000d3a191041}',
	_ta_orderrequest_value: '',
	inventories: [],
	clinic: {
		accountid: '61aea445-e3b4-ea11-a816-000d3a191041',
		name: 'A Podiatry Clinic',
	},
	orderRequest: undefined,
});

/**
 * Creates mock OrderReturn data for the tutorial
 * @param index - The index of the mock Return to create (0-9)
 * @returns A mock OrderReturn object
 */
export const createMockReturn = (index: number): OrderReturn => {
	const baseDate = new Date('2025-01-15');
	const expirationDate = new Date(baseDate);
	expirationDate.setDate(expirationDate.getDate() + 30 + index * 5);

	const dateReturned = new Date(baseDate);
	dateReturned.setDate(dateReturned.getDate() - index * 3);

	const newShippingDate = new Date(baseDate);
	newShippingDate.setDate(newShippingDate.getDate() + 7 + index * 2);

	// Product variations
	const products = [
		{ name: 'Axolotl Dual Graft (Q4332)', qCode: 'Q4332', id: 'prod-001' },
		{ name: 'Amnio-Maxx (Q4239)', qCode: 'Q4239', id: 'prod-002' },
		{ name: 'Helicoll (Q4164)', qCode: 'Q4164', id: 'prod-003' },
		{ name: 'DermaMatrix (Q4123)', qCode: 'Q4123', id: 'prod-004' },
		{ name: 'AlloDerm (Q4056)', qCode: 'Q4056', id: 'prod-005' },
	];

	// Clinic variations
	const clinics = [
		{ id: '61aea445-e3b4-ea11-a816-000d3a191041', name: 'A Podiatry Clinic' },
		{
			id: '72bfb556-f4c5-fb22-b927-000d3a191042',
			name: 'B Better Health & Wellness',
		},
		{
			id: '83cgc667-g5d6-gc33-ca38-000d3a191043',
			name: 'Podiatry Care Clinic',
		},
		{
			id: '94dhd778-h6e7-hd44-db49-000d3a191044',
			name: 'Advanced Foot & Ankle',
		},
		{
			id: 'a5eie889-i7f8-ie55-ec5a-000d3a191045',
			name: 'Coastal Podiatry Group',
		},
	];

	// Status variations
	const statuses = [
		953870000, // Pending
		953870001, // Submitted
		953870002, // In Transit
		953870003, // Received
		953870004, // Completed
	];
	const status = statuses[index % statuses.length];

	// Address variations - more realistic addresses like in QA
	const addresses = [
		{ street: '1000 Peachtree Streett', city: 'Atlanta' },
		{ street: '124 Athens Street', city: 'Athens' },
		{ street: '5100 John D', city: 'San Antonio' },
		{ street: '4541 N Josey Ln #110', city: 'Carrollton' },
		{ street: '789 Pine Road', city: 'Chicago' },
		{ street: '321 Elm Street', city: 'Houston' },
		{ street: '654 Maple Drive', city: 'Phoenix' },
	];

	const product = products[index % products.length];
	const clinic = clinics[index % clinics.length];
	const address = addresses[index % addresses.length];
	const tissueId = `TID-${String(1000 + index).padStart(4, '0')}`;

	const returnId = crypto.randomUUID();

	const baseTemplate = getBaseReturnTemplate();

	return {
		...baseTemplate,
		ta_returnrequestid: returnId,
		ta_name: `Return Request ${index + 1}`,
		ta_streetaddress: address.street,
		ta_city: address.city,
		ta_expirationdate: expirationDate.toISOString().split('T')[0],
		ta_datereturnedtransferred: dateReturned.toISOString().split('T')[0],
		ta_newshippingdaterequested: newShippingDate.toISOString().split('T')[0],
		ta_status: status,
		_ta_customername_value: `{${clinic.id}}`,
		inventories: [
			{
				ta_inventoryid: `inv-${index}`,
				ta_tissueid: tissueId,
				_ta_product_value: `{${product.id}}`,
				_ta_clinic_value: `{${clinic.id}}`,
				product: {
					ta_productid: product.id,
					ta_name: product.name,
					ta_description: `${product.name} Description`,
					ta_qcode: product.qCode,
				},
			},
		],
		clinic: {
			accountid: clinic.id,
			name: clinic.name,
		},
	};
};

/**
 * Generates an array of mock OrderReturn data for the tutorial
 * @param count - Number of mock Returns to generate (default: 10)
 * @returns Array of mock OrderReturn objects
 */
export const generateMockReturnData = (count = 10): OrderReturn[] => {
	return Array.from({ length: count }, (_, index) => createMockReturn(index));
};
